import type { ResultsCacheClient } from "@timescale/popsql-query-widget/query-client";

let resultsCacheClient: ResultsCacheClient | null = null;

/**
 * Keeps the results worker client of the results cache provider reachable
 * outside of React. Must stay a stable function: the provider re-creates the
 * client when its `onInit` prop changes.
 */
export const registerResultsCacheClient = (client: ResultsCacheClient) => {
  resultsCacheClient = client;
};

/**
 * Closes the database sessions of the query widgets, given by their
 * `sessionKeys` (see `explorerQuerySessionKey`). The widgets keep their
 * sessions open while the user moves around the console, so only logging out
 * closes them. The worker closes each session with an authenticated request,
 * so this must finish before the auth session ends.
 */
export const closeSqlWidgetSessions = async (sessionKeys: string[]) => {
  if (!resultsCacheClient) {
    return;
  }
  try {
    await resultsCacheClient.rpc({
      data: { sessionKeys },
      type: "closeSessions",
    });
  } catch (error) {
    console.error("Error closing the query widget database sessions:", error);
  }
};

/**
 * Releases what an explorer query tab holds in the worker when the tab is
 * closed: its database session, and the cached results of its last run. The
 * widget itself keeps both while the user moves around the console, and only
 * replaces the results when it starts the next run.
 */
export const closeSqlWidgetSession = async (
  sessionKey: string,
  lastRunId?: string,
) => {
  await closeSqlWidgetSessions([sessionKey]);
  if (!resultsCacheClient || !lastRunId) {
    return;
  }
  try {
    await resultsCacheClient.rpc({
      data: { runId: lastRunId },
      type: "deleteRun",
    });
  } catch (error) {
    console.error("Error deleting the query widget results:", error);
  }
};
